import { AuthGuard } from '@/components/auth/auth-guard';
import { AppLayout } from '@/components/layout';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Pagination } from '@/features/people/components';
import {
  CreateProjectDialog,
  ProjectCard,
  ProjectStatsCards,
} from '@/features/projects/components';
import { useDebouncedValue } from '@/hooks/use-debounce';
import { useArchiveProject, useProjectMembers, useProjects, useProjectStats, useUpdateProject } from '@/hooks/use-projects';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { cn } from '@/lib/utils';
import type { ProjectListItem } from '@hygge/shared';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuthStore } from '@/stores/auth';
import { createFileRoute, Outlet, redirect, useLocation, useNavigate } from '@tanstack/react-router';
import { hasPlaceholderPermission, usePermission } from '@/lib/permissions';
import { ChevronDown, ChevronRight, FolderPlus, LayoutGrid, List, Search } from 'lucide-react';
import { useState } from 'react';
import { useBoards } from '@/hooks/use-boards';
import { httpStatus } from '@/lib/http-status';
import { ArchiveBoardsChoice } from '@/features/projects/components/archive-boards-choice';
import { boardsReaderMaySet } from '@/features/projects/lib/project-boards';
import { useFreshOnOpen } from '@/features/projects/hooks/use-fresh-on-open';
import type { ArchiveBoardChoice } from '@/lib/projects';

type ViewMode = 'cards' | 'list';
const VIEW_MODE_KEY = 'project-view-mode';

function getStoredViewMode(): ViewMode {
  if (typeof window === 'undefined') return 'cards';
  return (localStorage.getItem(VIEW_MODE_KEY) as ViewMode) || 'cards';
}

export const Route = createFileRoute('/projects')({
  beforeLoad: async () => {
    const { isAuthenticated, isInitialized } = useAuthStore.getState();
    if (!isInitialized) return;
    if (!isAuthenticated) throw redirect({ to: '/login' });

    const user = useAuthStore.getState().user;
    if (hasPlaceholderPermission(user)) throw redirect({ to: '/placeholder' });
  },
  component: ProjectsPage,
});

function ProjectsPage() {
  const location = useLocation();
  const hasChildRoute = location.pathname !== '/projects';

  return (
    <AuthGuard>
      <AppLayout>
        {hasChildRoute ? <Outlet /> : <ProjectsContent />}
      </AppLayout>
    </AuthGuard>
  );
}

function ProjectsContent() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<string>('asc');
  const [viewMode, setViewMode] = useState<ViewMode>(getStoredViewMode);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<ProjectListItem | null>(null);
  const [archiveBoards, setArchiveBoards] = useState<ArchiveBoardChoice | null>(null);
  const {
    data: archiveTargetVisibleBoards = [],
    isError: isArchiveBoardsError,
    error: archiveBoardsError,
    refetch: refetchArchiveBoards,
  } = useBoards(
    { projectId: archiveTarget?.id },
    { enabled: !!archiveTarget },
  );
  // The archive question waits for a list fetched afresh when it opens.
  const archiveBoardsFresh = useFreshOnOpen(archiveTarget?.id ?? null, refetchArchiveBoards);
  const isLoadingArchiveBoards = !!archiveTarget && !archiveBoardsFresh;
  // Asked only about the boards the archive would actually change.
  const archiveTargetBoards = boardsReaderMaySet(archiveTargetVisibleBoards);
  // Refused outright (no Open Boards) is an answer — there is nothing to ask about.
  const archiveBoardsLoadFailed = isArchiveBoardsError && httpStatus(archiveBoardsError) !== 403;
  const closeArchive = () => {
    setArchiveTarget(null);
    setArchiveBoards(null);
  };
  const [unarchiveTarget, setUnarchiveTarget] = useState<ProjectListItem | null>(null);
  const debouncedSearch = useDebouncedValue(search, 300);
  const canCreate = usePermission('projects.create');

  const limit = viewMode === 'cards' ? 12 : 20;

  const queryParams = {
    page,
    limit,
    search: debouncedSearch || undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    source: sourceFilter !== 'all' ? sourceFilter : undefined,
    sort: sortField,
    order: sortOrder,
  };

  const { data, isLoading } = useProjects(queryParams);
  const { data: stats, isLoading: isLoadingStats } = useProjectStats();
  const archiveMutation = useArchiveProject();
  const updateMutation = useUpdateProject();

  const projects = data?.data ?? [];
  const totalPages = data?.meta?.totalPages ?? 0;
  const total = data?.meta?.total ?? 0;

  const activeFilterCount = [statusFilter !== 'all', sourceFilter !== 'all'].filter(Boolean).length;

  const handleViewMode = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem(VIEW_MODE_KEY, mode);
    setPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleOpen = (id: string) => {
    navigate({ to: '/projects/$id', params: { id } });
  };

  const handleArchive = async () => {
    if (!archiveTarget) return;
    await archiveMutation.mutateAsync({ id: archiveTarget.id, boards: archiveBoards ?? undefined });
    closeArchive();
  };

  const handleUnarchive = async () => {
    if (!unarchiveTarget) return;
    await updateMutation.mutateAsync({ id: unarchiveTarget.id, status: 'active' });
    setUnarchiveTarget(null);
  };

  return (
    <>
      {/* Breadcrumb */}
      <header className="sticky top-14 z-30 flex h-12 items-center border-b border-border bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/60 px-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Home</span>
          <span>/</span>
          <span className="font-medium text-foreground">Projects</span>
        </div>
      </header>

      <div className="p-6">
        {/* Title + Create */}
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-xl font-semibold tracking-tight text-foreground">Projects</h1>
          {canCreate && (
            <Button
              onClick={() => setIsCreateOpen(true)}
              className="gap-2 cursor-pointer shadow-sm hover:shadow transition-shadow"
            >
              <FolderPlus className="h-4 w-4" />
              <span>New</span>
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="mb-5">
          <ProjectStatsCards stats={stats} isLoading={isLoadingStats} />
        </div>

        {/* Toolbar */}
        <div className="mb-4 flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative max-w-sm flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search projects..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="flex-1" />

          {/* Filters */}
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[130px] h-9 text-xs">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="on_hold">On Hold</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sourceFilter} onValueChange={(v) => { setSourceFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[130px] h-9 text-xs">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
                <SelectItem value="peopleforce">PeopleForce</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={`${sortField}:${sortOrder}`}
              onValueChange={(v) => {
                const [f, o] = v.split(':');
                setSortField(f);
                setSortOrder(o);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[150px] h-9 text-xs">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name:asc">Name A-Z</SelectItem>
                <SelectItem value="name:desc">Name Z-A</SelectItem>
                <SelectItem value="createdAt:desc">Newest first</SelectItem>
                <SelectItem value="createdAt:asc">Oldest first</SelectItem>
                <SelectItem value="memberCount:desc">Most members</SelectItem>
              </SelectContent>
            </Select>

            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5">
                {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''}
              </Badge>
            )}

            {/* View toggle */}
            <div className="flex items-center rounded-md border border-border bg-muted/30 p-1">
              <Button
                variant="ghost"
                size="icon"
                className={cn('h-7 w-7 cursor-pointer', viewMode === 'cards' && 'bg-background shadow-sm')}
                onClick={() => handleViewMode('cards')}
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className={cn('h-7 w-7 cursor-pointer', viewMode === 'list' && 'bg-background shadow-sm')}
                onClick={() => handleViewMode('list')}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <ProjectsSkeleton viewMode={viewMode} />
        ) : projects.length === 0 ? (
          <EmptyState hasFilters={!!debouncedSearch || activeFilterCount > 0} onCreate={() => setIsCreateOpen(true)} />
        ) : viewMode === 'cards' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onOpen={handleOpen}
                onEdit={() => handleOpen(project.id)}
                onArchive={setArchiveTarget}
                onUnarchive={setUnarchiveTarget}
              />
            ))}
          </div>
        ) : (
          <ProjectListView
            projects={projects}
            onOpen={handleOpen}
          />
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-4">
            <Pagination page={page} totalPages={totalPages} total={total} limit={limit} onPageChange={setPage} itemLabel="projects" />
          </div>
        )}
      </div>

      {/* Create dialog */}
      <CreateProjectDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        onSuccess={(id) => handleOpen(id)}
      />

      {/* Archive confirmation */}
      <AlertDialog open={!!archiveTarget} onOpenChange={(open) => !open && closeArchive()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive Project</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to archive <strong>{archiveTarget?.name}</strong>? The project will be hidden from the default view but can be restored later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <ArchiveBoardsChoice
            boards={archiveTargetBoards}
            value={archiveBoards}
            onChange={setArchiveBoards}
            loadFailed={archiveBoardsLoadFailed}
          />
          <AlertDialogFooter>
            <AlertDialogCancel className="cursor-pointer">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleArchive}
              variant="destructive"
              className="cursor-pointer"
              // Not until the boards are in: an early click would archive without the question.
              disabled={
                archiveMutation.isPending ||
                isLoadingArchiveBoards ||
                archiveBoardsLoadFailed ||
                (archiveTargetBoards.length > 0 && !archiveBoards)
              }
            >
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Unarchive confirmation */}
      <AlertDialog open={!!unarchiveTarget} onOpenChange={(open) => !open && setUnarchiveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unarchive Project</AlertDialogTitle>
            <AlertDialogDescription>
              Restore <strong>{unarchiveTarget?.name}</strong> to active? It will reappear in the default project list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="cursor-pointer">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUnarchive}
              className="cursor-pointer"
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Restoring...' : 'Unarchive'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function ProjectListView({
  projects,
  onOpen,
}: {
  projects: ProjectListItem[];
  onOpen: (id: string) => void;
}) {
  const formatName = useEmployeeNameFormatter();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const statusConfig: Record<string, { label: string; className: string }> = {
    active: { label: 'Active', className: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' },
    on_hold: { label: 'On Hold', className: 'bg-amber-500/10 text-amber-600 border-amber-500/20' },
    archived: { label: 'Archived', className: 'bg-zinc-500/10 text-zinc-500 border-zinc-500/20' },
  };

  const toggleExpand = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setExpandedId((prev) => (prev === id ? null : id));
  };

  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/30">
            <th className="w-8 px-2 py-2.5" />
            <th className="text-left font-medium text-muted-foreground px-4 py-2.5">Project</th>
            <th className="text-left font-medium text-muted-foreground px-4 py-2.5">Status</th>
            <th className="text-left font-medium text-muted-foreground px-4 py-2.5">Manager</th>
            <th className="text-center font-medium text-muted-foreground px-4 py-2.5">Members</th>
            <th className="text-left font-medium text-muted-foreground px-4 py-2.5">Source</th>
          </tr>
        </thead>
        <tbody>
          {projects.map((project) => {
            const status = statusConfig[project.status] || statusConfig.active;
            const isExpanded = expandedId === project.id;
            return (
              <>
                <tr
                  key={project.id}
                  className={cn(
                    'border-b hover:bg-muted/30 cursor-pointer transition-colors',
                    isExpanded && 'bg-muted/20 border-b-0',
                  )}
                  onClick={() => onOpen(project.id)}
                >
                  <td className="px-2 py-3">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 cursor-pointer"
                      onClick={(e) => toggleExpand(e, project.id)}
                    >
                      {isExpanded
                        ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                        : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                    </Button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: project.color }} />
                      <div>
                        <p className="font-medium text-foreground">{project.name}</p>
                        <p className="text-xs text-muted-foreground font-mono">{project.slug}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0', status.className)}>
                      {status.label}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {project.managers.length === 0
                      ? '—'
                      : project.managers.length === 1
                        ? formatName({ firstName: project.managers[0].firstName, lastName: project.managers[0].lastName })
                        : `${formatName({ firstName: project.managers[0].firstName, lastName: project.managers[0].lastName })} +${project.managers.length - 1}`}
                  </td>
                  <td className="px-4 py-3 text-center text-muted-foreground">{project.memberCount}</td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                      {project.source === 'peopleforce' ? 'PF' : 'Manual'}
                    </Badge>
                  </td>
                </tr>
                {isExpanded && (
                  <tr key={`${project.id}-expanded`} className="border-b last:border-0">
                    <td colSpan={6} className="px-4 py-3 bg-muted/10">
                      <ExpandedMembersPanel projectId={project.id} />
                    </td>
                  </tr>
                )}
              </>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ExpandedMembersPanel({ projectId }: { projectId: string }) {
  const { data: allMembers, isLoading } = useProjectMembers(projectId);
  const formatName = useEmployeeNameFormatter();
  const members = (allMembers ?? []).filter((m) => !m.removedAt);

  if (isLoading) {
    return (
      <div className="flex gap-3 py-1">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex items-center gap-2 rounded-lg border border-border bg-background p-2.5 w-48">
            <div className="h-8 w-8 rounded-full bg-muted animate-pulse" />
            <div className="space-y-1.5">
              <div className="h-3 w-20 bg-muted animate-pulse rounded" />
              <div className="h-2.5 w-14 bg-muted animate-pulse rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (members.length === 0) {
    return <p className="text-xs text-muted-foreground py-1">No members assigned</p>;
  }

  return (
    <div className="flex flex-wrap gap-2 py-1">
      {members.map((member) => (
        <div
          key={member.id}
          className="flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 hover:border-border/80 transition-colors"
        >
          <Avatar className="h-8 w-8">
            <AvatarImage src={member.avatarUrl || undefined} />
            <AvatarFallback className="text-[10px]">
              {member.firstName[0]}{member.lastName[0]}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="text-xs font-medium text-foreground truncate">
              {formatName({ firstName: member.firstName, lastName: member.lastName })}
            </p>
            <div className="flex items-center gap-1.5">
              {member.departmentName && (
                <Badge variant="secondary" className="text-[9px] px-1 py-0 font-normal">
                  {member.departmentName}
                </Badge>
              )}
              {member.location && (
                <span className="text-[10px] text-muted-foreground truncate">{member.location}</span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ hasFilters, onCreate }: { hasFilters: boolean; onCreate: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted mb-4">
        <FolderPlus className="h-6 w-6 text-muted-foreground" />
      </div>
      <h3 className="text-sm font-medium text-foreground mb-1">
        {hasFilters ? 'No projects found' : 'No projects yet'}
      </h3>
      <p className="text-xs text-muted-foreground mb-4 max-w-sm">
        {hasFilters
          ? 'Try adjusting your search or filters.'
          : 'Create your first project or sync from PeopleForce.'}
      </p>
      {!hasFilters && (
        <Button onClick={onCreate} className="gap-2 cursor-pointer">
          <FolderPlus className="h-4 w-4" />
          Create Project
        </Button>
      )}
    </div>
  );
}

function ProjectsSkeleton({ viewMode }: { viewMode: ViewMode }) {
  if (viewMode === 'cards') {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="rounded-lg border border-border p-5 space-y-3">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-3 w-full" />
            <div className="flex justify-between pt-2">
              <Skeleton className="h-5 w-24" />
              <Skeleton className="h-5 w-16" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <div className="border-b bg-muted/30 px-4 py-2.5">
        <Skeleton className="h-4 w-full max-w-lg" />
      </div>
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="border-b last:border-0 px-4 py-3">
          <Skeleton className="h-4 w-full" />
        </div>
      ))}
    </div>
  );
}
